/**
 * Frontend mirror of the server's audit-action and target-type
 * vocabulary, used to populate the `<datalist>` suggestions on the
 * /admin/audit reader page (#429 Wave 3 Track G).
 *
 * **Source of truth:** `apps/server/src/infrastructure/audit/actions.ts`.
 * Drift is caught by the server-side `actions.test.ts` (specifically
 * the "Frontend mirror parity" block) — it reads THIS file as text
 * and fails if any server-side action or target-type string is missing
 * here. Server adds a new entry without updating this file → server
 * test fails. The frontend's own catalog-sanity test only enforces
 * the `resource.verb` shape, NOT parity — parity is enforced
 * server-side. PR #530 Copilot review.
 *
 * The reader UI uses these as `<datalist>` suggestions for filter
 * autocomplete; the filter inputs are free-text so a typo in this
 * file doesn't block an investigator from querying an unknown action
 * (an upgrade-mismatched front+back-end can still search for a new
 * action), but the suggestion list keeps the common-case discoverable.
 */

/** Every action key the server can emit. Mirrors the leaf values of
 * `AUDIT_ACTIONS` in `apps/server/src/infrastructure/audit/actions.ts`.
 * Keep alphabetized within each resource group for grep-ability. */
export const AUDIT_ACTION_KEYS: readonly string[] = [
  // appSettings
  'appSettings.updated',
  // auth
  'auth.password_changed',
  'auth.password_reset_link_created',
  'auth.session_revoked',
  // billing (#764, #780, #789, #1037, #1548, #1726)
  'billing.plan_assigned',
  'billing.force_reconciled',
  'billing.credits_granted',
  'billing.plan_created',
  'billing.plan_edited',
  'billing.plan_published',
  'billing.plan_renamed',
  'billing.tier_changed',
  'billing.tier_defaults_changed',
  'billing.usage_recomputed',
  // credential
  'credential.read',
  'credential.written',
  // impersonation
  'impersonation.started',
  'impersonation.stopped',
  // invitation (#1024 Slice 3 — admin tier cross-org list)
  'invitation.list_viewed',
  // member
  'member.invitation_accepted',
  'member.invitation_canceled',
  'member.invitation_rejected',
  'member.invited',
  'member.removed',
  'member.role_changed',
  'member.self_left',
  // org
  'org.created',
  'org.recovered',
  'org.account_set',
  'org.soft_deleted',
  'org.hard_deleted',
  'org.updated',
  // single sign-on (#1792)
  'sso.connection_created',
  'sso.connection_removed',
  'sso.connection_updated',
  'sso.default_workspaces_updated',
  // pricing
  'pricing.capability_overridden',
  'pricing.updated',
  // prompt (#574)
  'prompt.comment_added',
  'prompt.comment_removed',
  'prompt.created',
  'prompt.published',
  'prompt.tags_updated',
  'prompt.version_created',
  // rag (#601)
  'rag.inspector_query',
  // slack (#1048)
  'slack.channel_added',
  'slack.channel_removed',
  'slack.delivery_failed',
  'slack.delivery_succeeded',
  'slack.disconnected',
  'slack.installed',
  'slack.integration_broken_detected',
  'slack.routing_row_added',
  'slack.routing_row_removed',
  'slack.routing_row_updated',
  'slack.routing_rows_added',
  'slack.subscriptions_updated',
  'slack.test_message_sent',
  // testDataSet (#1258 Slice 2 — unified Test Data surface)
  'testDataSet.column_added',
  'testDataSet.column_default_set',
  'testDataSet.column_deleted',
  'testDataSet.column_fresh_per_run_set',
  'testDataSet.column_renamed',
  'testDataSet.column_retyped',
  'testDataSet.consumed_reset',
  'testDataSet.created',
  'testDataSet.csv_exported',
  'testDataSet.csv_imported',
  'testDataSet.deleted',
  'testDataSet.renamed',
  'testDataSet.sensitive_revealed',
  // dataBinding (#1258 Slice 3 — mapper + binding config)
  'dataBinding.created',
  'dataBinding.config_updated',
  'dataBinding.deleted',
  'dataBinding.mapper_updated',
  // scheduledJob (#1278 / #1279 — admin-tier scheduled-jobs catalog)
  'scheduledJob.config_updated',
  'scheduledJob.deleted',
  'scheduledJob.disabled',
  'scheduledJob.paused',
  'scheduledJob.resumed',
  'scheduledJob.run_now',
  // test plans (#2369 — a plan copied into another variant)
  'testPlan.copied',
  // superadmin
  'superadmin.access',
  // superadmin grants (#1022 Slice 1)
  'superadmin.grant_created',
  'superadmin.grant_revoked',
  // operator tokens (#1573 — headless automation credential)
  'operatorToken.created',
  'operatorToken.revoked',
  // user (#1023 Slice 2 — global Users admin surface, audit-on-read;
  // #1542 added the orphan hard-delete write)
  'user.detail_viewed',
  'user.list_viewed',
  'user.deleted',
  'user.deletion_scheduled',
  'user.deletion_cancelled',
  // team (#526 PR 4)
  'team.created',
  'team.deleted',
  'team.member_added',
  'team.member_removed',
  'team.updated',
  // workspaceMember (#526 PR 1)
  'workspaceMember.granted',
  'workspaceMember.revoked',
  'workspaceMember.role_changed',
]

/** Every target-type string the server can emit. Mirrors
 * `AUDIT_TARGET_TYPES` in the same server file. */
export const AUDIT_TARGET_TYPE_KEYS: readonly string[] = [
  'app_settings',
  'credential',
  'invitation',
  'member',
  'model_pricing',
  'operator_token',
  'org',
  'plan',
  'prompt',
  'route',
  'scheduledJob',
  'session',
  'slack_install',
  'slack_integration',
  'slack_routing_rule',
  'data_binding',
  'team',
  'test_data_migration_review',
  'test_data_set',
  'test_plan',
  'user',
  'variant',
  'variant_test_data',
  'sso_connection',
  'workspace',
]
